/**
 * intelClient — typed wrappers for the four cycle-102 sprint-1 intel
 * inspector endpoints (EII, Convergence, Focal Points, Calibration).
 *
 * Each fetcher returns a runtime-validated payload (zod) so the
 * inspector panels can render fallback UI when the backend drifts
 * (graceful degradation per SDD §6.4). When validation fails the
 * fetcher throws — the inspector's TanStack Query error branch then
 * renders the appropriate fallback chip.
 *
 * Documented surface: see `frontend/src/api/README.md`.
 */

import { z } from 'zod';
import { apiClient } from './client';

// ─── Shared error-state ─────────────────────────────────────────────

const ErrorStateKindSchema = z.enum([
  'stale',
  'missing_input',
  'compute_failure',
]);

const ErrorStateSchema = z
  .object({
    kind: ErrorStateKindSchema,
    message: z.string(),
  })
  .nullable();

export type IntelErrorStateKind = z.infer<typeof ErrorStateKindSchema>;
export type IntelErrorState = z.infer<typeof ErrorStateSchema>;

// ─── EII ────────────────────────────────────────────────────────────

export const EIIBreakdownSchema = z.object({
  base_cii: z.number(),
  theatre_adj: z.number(),
  paradox_adj: z.number(),
  aeon_adj: z.number(),
  // null while the source-quality modifier is not applied (P4, #488).
  source_quality_multiplier: z.number().nullable(),
});

export const EIIResponseSchema = z.object({
  eii_score: z.number(),
  breakdown: EIIBreakdownSchema,
  epistemic_status: z.enum(['Inferred', 'Corroborated', 'Verified']),
  trend: z.enum(['Stable', 'Escalating', 'De-escalating']),
  region: z.string(),
  computed_at: z.string(),
  // Cycle-102 sprint-1 additive fields. Defaulted so older payloads still parse.
  freshness_seconds: z.number().int().nonnegative().default(0),
  error_state: ErrorStateSchema.default(null),
});

export type EIIBreakdown = z.infer<typeof EIIBreakdownSchema>;
export type EIIResponse = z.infer<typeof EIIResponseSchema>;

/**
 * Each component score in the EII breakdown gets a derivation_level tag
 * per PRD F-1.2. `composite_external` and `composite_echelon_final` are
 * context-only and never weighted into the displayed EII.
 */
export type DerivationLevel =
  | 'raw'
  | 'collector_normalised'
  | 'composite_external'
  | 'composite_echelon_final';

export interface EIIInspectorRow {
  label: string;
  value: number;
  derivationLevel: DerivationLevel;
  weightedInComposite: boolean;
  contextOnly: boolean;
}

/**
 * Decompose an EII response into renderable rows. The composite EII is
 * surfaced as a `composite_echelon_final` row marked context-only — when
 * fed back into a downstream inspector (the testable case from the sprint
 * plan AC#1.rawness) it must visibly NOT contribute to the new score.
 */
export function buildEIIInspectorRows(payload: EIIResponse): EIIInspectorRow[] {
  return [
    {
      label: 'Base CII',
      value: payload.breakdown.base_cii,
      derivationLevel: 'composite_external',
      weightedInComposite: false,
      contextOnly: true,
    },
    {
      label: 'Theatre adj',
      value: payload.breakdown.theatre_adj,
      derivationLevel: 'collector_normalised',
      weightedInComposite: true,
      contextOnly: false,
    },
    {
      label: 'Paradox adj',
      value: payload.breakdown.paradox_adj,
      derivationLevel: 'collector_normalised',
      weightedInComposite: true,
      contextOnly: false,
    },
    {
      label: 'Aeon adj',
      value: payload.breakdown.aeon_adj,
      derivationLevel: 'collector_normalised',
      weightedInComposite: true,
      contextOnly: false,
    },
    ...(payload.breakdown.source_quality_multiplier === null
      ? []
      : [
          {
            label: 'Source quality multiplier',
            value: payload.breakdown.source_quality_multiplier,
            derivationLevel: 'collector_normalised' as const,
            weightedInComposite: true,
            contextOnly: false,
          },
        ]),
    {
      label: 'Composite EII',
      value: payload.eii_score,
      derivationLevel: 'composite_echelon_final',
      weightedInComposite: false,
      contextOnly: true,
    },
  ];
}

export async function fetchEII(region: string): Promise<EIIResponse> {
  const { data } = await apiClient.get(`/api/v1/eii/${encodeURIComponent(region)}`);
  return EIIResponseSchema.parse(data);
}

// ─── Convergence ────────────────────────────────────────────────────

export const ConvergenceHeatmapCellSchema = z.object({
  lat: z.number(),
  lon: z.number(),
  density: z.number(),
  events: z.number().int().nonnegative(),
  sources: z.array(z.string()),
  theatres: z.array(z.string()),
});

export const ConvergenceHeatmapResponseSchema = z.object({
  cells: z.array(ConvergenceHeatmapCellSchema),
  grid_size: z.number().int(),
});

export type ConvergenceHeatmapCell = z.infer<typeof ConvergenceHeatmapCellSchema>;
export type ConvergenceHeatmapResponse = z.infer<
  typeof ConvergenceHeatmapResponseSchema
>;

/**
 * Frontend-side convergence cell view (PRD §5.7.2). Adapter over the
 * existing `/convergence/heatmap` payload — sprint-1 does not change
 * the backend. A cell is "converged" when its `sources` list contains
 * 3+ distinct entries, satisfying AC#2.
 */
export interface ConvergenceCellView {
  cellKey: string;
  lat: number;
  lon: number;
  sourceGroups: readonly string[];
  signals: number;
  theatres: readonly string[];
  density: number;
  isConverged: boolean;
}

export const CONVERGENCE_THRESHOLD_SOURCE_GROUPS = 3;

export function adaptConvergenceCells(
  payload: ConvergenceHeatmapResponse,
): ConvergenceCellView[] {
  return payload.cells.map((cell) => {
    const groups = Array.from(new Set(cell.sources));
    return {
      cellKey: `${cell.lat.toFixed(3)}|${cell.lon.toFixed(3)}`,
      lat: cell.lat,
      lon: cell.lon,
      sourceGroups: groups,
      signals: cell.events,
      theatres: cell.theatres,
      density: cell.density,
      isConverged: groups.length >= CONVERGENCE_THRESHOLD_SOURCE_GROUPS,
    };
  });
}

export async function fetchConvergenceHeatmap(): Promise<ConvergenceHeatmapResponse> {
  const { data } = await apiClient.get('/api/v1/convergence/heatmap');
  return ConvergenceHeatmapResponseSchema.parse(data);
}

// ─── Focal Points ──────────────────────────────────────────────────

export const FocalPointScoreBreakdownSchema = z.object({
  signal: z.number(),
  significance: z.number(),
  theatre: z.number(),
  paradox: z.number(),
  aeon: z.number(),
});

export const FocalPointSchema = z.object({
  h3_cell: z.string(),
  geo_label: z.string(),
  focal_score: z.number(),
  urgency: z.string(),
  signal_count: z.number().int().nonnegative(),
  domain_count: z.number().int().nonnegative(),
  domains: z.array(z.string()),
  score_breakdown: FocalPointScoreBreakdownSchema,
  suggested_action: z.string(),
  contributing_signal_ids: z.array(z.string()),
});

export const FocalPointsResponseSchema = z.object({
  focal_points: z.array(FocalPointSchema),
  total: z.number().int().nonnegative(),
});

export type FocalPoint = z.infer<typeof FocalPointSchema>;
export type FocalPointsResponse = z.infer<typeof FocalPointsResponseSchema>;

export interface FocalPointsParams {
  lookbackHours?: number;
  limit?: number;
}

export async function fetchFocalPoints(
  params: FocalPointsParams = {},
  signal?: AbortSignal,
): Promise<FocalPointsResponse> {
  const { data } = await apiClient.get('/api/v1/osint/signals/focal-points', {
    params: {
      lookback_hours: params.lookbackHours ?? 24,
      limit: params.limit ?? 10,
    },
    signal,
  });
  return FocalPointsResponseSchema.parse(data);
}

// ─── Calibration ───────────────────────────────────────────────────

export const CalibrationSkillSchema = z.object({
  skill_id: z.string(),
  model_id: z.string(),
  confidence: z.number(),
  base_rate_correct: z.number().int().nonnegative(),
  base_rate_total: z.number().int().nonnegative(),
  last_run_at: z.string().nullable(),
  freshness_seconds: z.number().int().nonnegative(),
  error_state: ErrorStateSchema.default(null),
});

export const CalibrationSkillsResponseSchema = z.object({
  skills: z.array(CalibrationSkillSchema),
  lookback_days: z.number().int().positive(),
  computed_at: z.string(),
});

export type CalibrationSkill = z.infer<typeof CalibrationSkillSchema>;
export type CalibrationSkillsResponse = z.infer<
  typeof CalibrationSkillsResponseSchema
>;

export interface CalibrationParams {
  topN?: number;
  lookbackDays?: number;
}

export async function fetchCalibrationSkills(
  params: CalibrationParams = {},
): Promise<CalibrationSkillsResponse> {
  const { data } = await apiClient.get('/api/v1/calibration/skills', {
    params: {
      top_n: params.topN ?? 10,
      lookback_days: params.lookbackDays ?? 30,
    },
  });
  return CalibrationSkillsResponseSchema.parse(data);
}

// ─── Stale-chip helper ─────────────────────────────────────────────

/**
 * Inspector panels surface a stale chip when freshness > 5 minutes
 * (SDD §6.4). Single source of truth so the threshold can move without
 * touching every panel.
 */
export const STALE_FRESHNESS_THRESHOLD_SECONDS = 300;

export function isStale(freshnessSeconds: number): boolean {
  return freshnessSeconds > STALE_FRESHNESS_THRESHOLD_SECONDS;
}

// ─── External Intelligence (cycle-105 sprint-1 Task 3 / 3n8j.1) ────
//
// Sibling of useEII / useConvergence / useFocalPoints. Renders WorldMonitor
// composite signals in a walled-off rail. Bands-only — the numeric
// composite_value is NEVER in the response (backend strips it, FR-1.2.a).
//
// Post-bug-190 contract (017961e1): 5 capitalized bands, per-region rows,
// region is load-bearing for the row label, no client-side cohort.

export const BAND_VALUES = ['Low', 'Elevated', 'High', 'Critical', 'unavailable'] as const;
export type ExternalIntelBand = (typeof BAND_VALUES)[number];

export const FRESHNESS_TIER_VALUES = ['fresh', 'aging', 'stale', 'expired'] as const;
export type ExternalIntelFreshnessTier = (typeof FRESHNESS_TIER_VALUES)[number];

export const AVAILABILITY_REASON_VALUES = [
  'available',
  'invalid_provenance',
  'stale',
  'schema_version_unknown',
  'upstream_down',
] as const;
export type ExternalIntelAvailabilityReason = (typeof AVAILABILITY_REASON_VALUES)[number];

export const CompositeRowSchema = z.object({
  composite_type: z.string().max(64),
  band: z.enum(BAND_VALUES),
  producer: z.string().max(64).nullable().optional().default(null),
  producer_baseline_v: z.string().max(64).nullable().optional().default(null),
  emitted_at: z.string().datetime().nullable().optional().default(null),
  fetched_at: z.string().datetime().nullable().optional().default(null),
  freshness_tier: z.enum(FRESHNESS_TIER_VALUES),
  availability_reason: z.enum(AVAILABILITY_REASON_VALUES),
  external_id: z.string().max(128).nullable().optional().default(null),
  composed_of: z.number().int().positive().nullable().optional().default(null),
  tooltip_text: z.string().max(256).nullable().optional().default(null),
  region: z.string().max(32).nullable().optional().default(null),
});

export const PanelMetadataSchema = z.object({
  feature_flag_enabled: z.boolean(),
  schema_version: z.string().max(8).default('v1'),
  context_filter_applied: z.boolean().default(false),
});

export const ListCompositesResponseSchema = z.object({
  composites: z.array(CompositeRowSchema).default([]),
  panel_metadata: PanelMetadataSchema,
});

export type ExternalIntelCompositeRow = z.infer<typeof CompositeRowSchema>;
export type ExternalIntelPanelMetadata = z.infer<typeof PanelMetadataSchema>;
export type ListCompositesResponse = z.infer<typeof ListCompositesResponseSchema>;

/** Investigation- or signal-scoped panel context. Single-operator threat
 * model means cross-context exposure within a session is acceptable per
 * PRD §3.5 — context is still passed so cohort recording is honest. */
export type ExternalIntelContext =
  | { kind: 'investigation'; id: string }
  | { kind: 'signal'; id: string };

function formatContext(ctx: ExternalIntelContext): string {
  return `${ctx.kind}:${ctx.id}`;
}

/** Sprint-1 retains the silent-skip detection minimally; sprint-3 (3n8j.3)
 * wires the audit-skip beacon. Distinguishing 503/401 from hard errors lets
 * the panel render `null` (no DOM) without an error chip — that's the
 * feature-flag UX contract. */
export interface FetchExternalIntelligenceResult {
  /** Parsed payload when the request succeeded. `null` for silent-skip. */
  data: ListCompositesResponse | null;
  /** True when the server returned 503 (feature flag off) or 401
   * (unauthenticated). Panel must render `null` — no skeleton, no
   * placeholder. */
  silentSkip: boolean;
}

export async function fetchExternalIntelligence(
  ctx: ExternalIntelContext,
): Promise<FetchExternalIntelligenceResult> {
  try {
    const response = await apiClient.get('/api/v1/intelligence/external', {
      params: { context: formatContext(ctx) },
    });
    return {
      data: ListCompositesResponseSchema.parse(response.data),
      silentSkip: false,
    };
  } catch (err: unknown) {
    // Axios shape — narrow via duck-typing, no axios type import to keep
    // the surface decoupled from the client implementation.
    const status =
      typeof err === 'object' && err !== null && 'response' in err
        ? (err as { response?: { status?: number } }).response?.status
        : undefined;
    if (status === 503 || status === 401) {
      return { data: null, silentSkip: true };
    }
    throw err;
  }
}

/** Band severity, descending order — for sorting per-region rows so the
 * operator's eye lands on Critical first, then High, Elevated, Low,
 * unavailable last. Matches the post-bug-190 brief render rule §2. */
const BAND_SEVERITY: Record<ExternalIntelBand, number> = {
  Critical: 0,
  High: 1,
  Elevated: 2,
  Low: 3,
  unavailable: 4,
};

export function compareRowsBySeverity(
  a: ExternalIntelCompositeRow,
  b: ExternalIntelCompositeRow,
): number {
  const bandDelta = BAND_SEVERITY[a.band] - BAND_SEVERITY[b.band];
  if (bandDelta !== 0) return bandDelta;
  // Within same band, sort by region alpha-asc. Null regions sort last
  // so the operator's eye lands on labelled rows first.
  const regionA = a.region ?? '\uffff';
  const regionB = b.region ?? '\uffff';
  return regionA.localeCompare(regionB);
}

/** Aggregate availability for the panel header chip. Sprint-1 derives
 * this client-side; the post-bug-190 brief §What changed row 3 confirms
 * the backend does NOT emit a top-level availability field. */
export type PanelAvailability = 'live' | 'stale' | 'unavailable';

export function derivePanelAvailability(
  rows: readonly ExternalIntelCompositeRow[],
): PanelAvailability {
  if (rows.length === 0) return 'unavailable';
  const allUnavailable = rows.every((r) => r.band === 'unavailable');
  if (allUnavailable) return 'unavailable';
  const anyStaleOrWorse = rows.some(
    (r) => r.freshness_tier === 'stale' || r.freshness_tier === 'expired',
  );
  if (anyStaleOrWorse) return 'stale';
  return 'live';
}

// ─── Audit-skip beacon (cycle-105 sprint-1 Task 6-frontend / 3n8j.3) ─

/** Backend schema (Pydantic AuditSkipRequest): view_ctx + optional
 * investigation_id (max 36 chars) + nullable cohort fields written
 * server-side. Post-bug-190 removed the cohort-toggle interlock from
 * the GET response — the frontend posts only the context. */
export type ExternalIntelViewCtx = 'investigation_view' | 'signal_detail_view';

export interface AuditSkipPayload {
  view_ctx: ExternalIntelViewCtx;
  /** Investigation ID when context is investigation-scoped, null
   * otherwise. Signal context posts the beacon with `investigation_id:
   * null` so the cohort row is still recorded; the backend's AuditSkip
   * schema does not currently carry a `signal_id` field — a follow-up
   * bead extends it if signal-scoped cohort granularity matters. */
  investigation_id: string | null;
}

/** Derive the audit-skip payload from a panel context. Investigation
 * context maps directly. Signal context posts `investigation_view`
 * with no id; this matches the backend's current schema, and lets the
 * cohort be recorded against the surface the operator is on. */
export function contextToAuditSkipPayload(
  ctx: ExternalIntelContext,
): AuditSkipPayload {
  if (ctx.kind === 'investigation') {
    return { view_ctx: 'investigation_view', investigation_id: ctx.id };
  }
  return { view_ctx: 'signal_detail_view', investigation_id: null };
}

/** POST the silent-skip cohort row. The endpoint returns 204 when the
 * feature flag is ON (no-op), or 204 when the flag is OFF and the
 * cohort row is persisted. Failure is fire-and-forget — a network
 * error here MUST NOT surface to the operator (silent-skip means
 * silent). FR-1.5.c. */
export async function postAuditSkipBeacon(
  ctx: ExternalIntelContext,
): Promise<void> {
  const body = contextToAuditSkipPayload(ctx);
  try {
    await apiClient.post('/api/v1/intelligence/external/audit-skip', body);
  } catch {
    // Silent — the beacon is best-effort cohort recording. The audit
    // row not landing is degradation in FR-2.5 reconstruction, not an
    // operator-visible failure.
  }
}

// ─── Structural Context (cycle-114 sprint-213 Tasks 3.4/3.5) ──────────
//
// Read-only client for the non-scored Lane-3 panel. Mirrors the External
// Intelligence pattern: zod-validated, silent-skip on 503/401. Serves
// NON-SCORED structural priors (country resilience CRI + cable-health) —
// NEVER a scored/severity signal. The backend `ContextBlock` is
// field-whitelisted (no severity/velocity/event_significance can leak); the
// `block_type`/`context_type` literals are the anti-elevation surface (§3.8).

export const STRUCTURAL_SCORE_BANDS = [
  'low',
  'moderate',
  'elevated',
  'fragile',
  'unavailable',
] as const;
export type StructuralScoreBand = (typeof STRUCTURAL_SCORE_BANDS)[number];

export const StructuralCoverageSchema = z.object({
  source_coverage: z.number().min(0).max(1).default(0),
  imputation_ratio: z.number().min(0).max(1).default(0),
  low_confidence: z.boolean().default(false),
});

export const StructuralLineageSchema = z.object({
  derived_from: z.string().nullable().optional().default(null),
  freshness: z.string().nullable().optional().default(null),
});

export const StructuralSelectionFeaturesSchema = z.object({
  domains: z.array(z.string()).default([]),
  entities: z.array(z.string()).default([]),
  confidence: z.number().min(0).max(1).default(0),
});

export const StructuralContextBlockSchema = z.object({
  block_type: z.enum(['cri_profile', 'adapter_output']),
  context_type: z.enum(['cri', 'cable_health']),
  id: z.string(),
  country_code: z.string().nullable().optional().default(null),
  country_name: z.string().nullable().optional().default(null),
  as_of: z.string(),
  cri_score: z.number().nullable().optional().default(null),
  score_band: z.enum(STRUCTURAL_SCORE_BANDS).nullable().optional().default(null),
  pillar_scores: z.record(z.number()).nullable().optional().default(null),
  domain_scores: z.record(z.number()).nullable().optional().default(null),
  coverage: StructuralCoverageSchema.nullable().optional().default(null),
  gap_kind: z
    .enum(['SIGNAL_ABSENCE', 'INTELLIGENCE_GAP'])
    .nullable()
    .optional()
    .default(null),
  calibration_status: z.enum(['provisional', 'calibrated']).default('provisional'),
  calculation_version: z.string(),
  selection_features: StructuralSelectionFeaturesSchema.optional().default({
    domains: [],
    entities: [],
    confidence: 0,
  }),
  authority_tier: z.enum(['TIER_1', 'TIER_2']).nullable().optional().default(null),
  lineage: StructuralLineageSchema.nullable().optional().default(null),
  label: z.string().default('structural prior, not live event'),
});

export const ListStructuralContextResponseSchema = z.object({
  blocks: z.array(StructuralContextBlockSchema).default([]),
  generated_at: z.string(),
  truncated: z.boolean().default(false),
  cross_version: z.boolean().default(false),
});

export type StructuralContextBlock = z.infer<typeof StructuralContextBlockSchema>;
export type ListStructuralContextResponse = z.infer<
  typeof ListStructuralContextResponseSchema
>;

export interface StructuralContextParams {
  /** ISO-3166-1 alpha-3, e.g. `DEU`. Omit for all in-scope countries. */
  countryCode?: string;
  /** Filter to one lane shape. Omit for both. */
  contextType?: 'cri' | 'cable_health';
}

export interface FetchStructuralContextResult {
  /** Parsed payload when the request succeeded. `null` for silent-skip. */
  data: ListStructuralContextResponse | null;
  /** True when the server returned 503 (flag off) or 401 (unauthenticated).
   * Panel renders `null` — no skeleton, no placeholder. */
  silentSkip: boolean;
}

export async function fetchStructuralContext(
  params: StructuralContextParams = {},
): Promise<FetchStructuralContextResult> {
  const query: Record<string, string> = {};
  if (params.countryCode) query.country_code = params.countryCode;
  if (params.contextType) query.context_type = params.contextType;
  try {
    const response = await apiClient.get('/api/v1/structural-context', {
      params: query,
    });
    return {
      data: ListStructuralContextResponseSchema.parse(response.data),
      silentSkip: false,
    };
  } catch (err: unknown) {
    const status =
      typeof err === 'object' && err !== null && 'response' in err
        ? (err as { response?: { status?: number } }).response?.status
        : undefined;
    if (status === 503 || status === 401) {
      return { data: null, silentSkip: true };
    }
    throw err;
  }
}

/** Aggregate availability for the panel header chip. `unavailable` when there
 * are no blocks or every CRI block is band=unavailable / score-less; `stale`
 * when any block flags low_confidence; else `live`. Achromatic — this is NOT a
 * severity signal, only a freshness/coverage hint. */
export type StructuralAvailability = 'live' | 'stale' | 'unavailable';

export function deriveStructuralAvailability(
  blocks: readonly StructuralContextBlock[],
): StructuralAvailability {
  if (blocks.length === 0) return 'unavailable';
  const allUnavailable = blocks.every(
    (b) =>
      b.score_band === 'unavailable' ||
      (b.context_type === 'cri' && b.cri_score == null),
  );
  if (allUnavailable) return 'unavailable';
  const anyLowConfidence = blocks.some((b) => b.coverage?.low_confidence === true);
  if (anyLowConfidence) return 'stale';
  return 'live';
}
